/**
 * React Query hooks for asset management
 */

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { assetsApi } from '../services/api/assets';
import type { AssetCreateData, AssetUpdateData } from '../types/asset';
import { useEffectiveCompanyId } from './useEffectiveCompany';
import { useToast } from './useToast';

/**
 * Hook to list assets
 */
export const useAssets = (options?: { assetTypeId?: string; activeOnly?: boolean }) => {
  const companyId = useEffectiveCompanyId();

  return useQuery(
    ['assets', companyId, options],
    () => assetsApi.list(companyId, options),
    {
      enabled: !!companyId,
    }
  );
};

/**
 * Hook to get a single asset
 */
export const useAsset = (assetId: string) => {
  const companyId = useEffectiveCompanyId();

  return useQuery(
    ['assets', assetId, companyId],
    () => assetsApi.getById(assetId, companyId),
    {
      enabled: !!assetId && !!companyId,
    }
  );
};

/**
 * Hook to create an asset
 */
export const useCreateAsset = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    (data: AssetCreateData) => assetsApi.create(companyId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assets', companyId]);
        showSuccess('Activo creado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al crear activo');
      },
    }
  );
};

/**
 * Hook to update an asset
 */
export const useUpdateAsset = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    ({ assetId, data }: { assetId: string; data: AssetUpdateData }) =>
      assetsApi.update(assetId, companyId, data),
    {
      onSuccess: (updatedAsset) => {
        queryClient.invalidateQueries(['assets', companyId]);
        queryClient.setQueryData(['assets', updatedAsset.assetId, companyId], updatedAsset);
        showSuccess('Activo actualizado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al actualizar activo');
      },
    }
  );
};

/**
 * Hook to delete an asset
 */
export const useDeleteAsset = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    (assetId: string) => assetsApi.delete(assetId, companyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assets', companyId]);
        showSuccess('Activo eliminado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al eliminar activo');
      },
    }
  );
};

/**
 * Hook to deactivate an asset
 */
export const useDeactivateAsset = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    (assetId: string) => assetsApi.deactivate(assetId, companyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assets', companyId]);
        showSuccess('Activo desactivado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al desactivar activo');
      },
    }
  );
};
